import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import Input from 'components/ui/Input';
import Button from 'components/ui/Button';

const SEARCH_SUGGESTIONS = [
  "Apple iPhone 15 Pro",
  "Samsung 4K Smart TV 55\"",
  "Sony WH-1000XM5 Headphones",
  "Nike Air Max 270",
  "Instant Pot Duo 7-in-1",
  "Dyson V15 Vacuum",
];

export default function ManualSearchPanel({ isOpen, onClose, onProductSelect, mockProducts }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  const handleSearch = () => {
    if (!query?.trim()) return;
    const filtered = mockProducts?.filter(p =>
      p?.name?.toLowerCase()?.includes(query?.toLowerCase()) ||
      p?.brand?.toLowerCase()?.includes(query?.toLowerCase())
    );
    setResults(filtered?.length > 0 ? filtered : mockProducts?.slice(0, 3));
    setSearched(true);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') handleSearch();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-30 flex flex-col"
          style={{ background: 'rgba(2,6,23,0.97)', backdropFilter: 'blur(20px)' }}
        >
          <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-slate-800">
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center">
              <Icon name="ArrowLeft" size={20} color="#94A3B8" />
            </button>
            <h3 className="font-heading font-semibold text-slate-100">Manual Product Search</h3>
          </div>

          <div className="px-4 pt-4 pb-2">
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="search"
                  placeholder="Search product name or brand..."
                  value={query}
                  onChange={(e) => setQuery(e?.target?.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <Button variant="default" size="default" iconName="Search" onClick={handleSearch} className="mt-0 self-end mb-0">
                Search
              </Button>
            </div>
          </div>

          {!searched && (
            <div className="px-4 py-3">
              <p className="text-xs text-slate-500 mb-2 font-caption">Suggestions</p>
              <div className="flex flex-wrap gap-2">
                {SEARCH_SUGGESTIONS?.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { setQuery(s); }}
                    className="text-xs bg-slate-800 text-slate-300 px-3 py-1.5 rounded-full border border-slate-700 hover:border-cyan-500/40 hover:text-cyan-400 transition-colors min-h-[36px]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {searched && (
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {results?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="SearchX" size={40} color="#475569" />
                  <p className="text-slate-500 mt-3">No products found</p>
                </div>
              ) : (
                results?.map((product) => (
                  <button
                    key={product?.id}
                    onClick={() => { onProductSelect(product); onClose(); }}
                    className="w-full flex items-center gap-4 bg-slate-800/60 rounded-xl p-3 border border-slate-700 hover:border-cyan-500/40 transition-colors text-left min-h-[44px]"
                  >
                    <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-slate-700">
                      <img src={product?.image} alt={product?.imageAlt} className="w-full h-full object-cover" onError={e => { e.target.src = '/assets/images/no_image.png'; }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-200 line-clamp-1">{product?.name}</p>
                      <p className="text-xs text-slate-500">{product?.brand}</p>
                      <span className="font-data text-base font-bold text-cyan-400">${product?.price?.toFixed(2)}</span>
                    </div>
                    <Icon name="ChevronRight" size={18} color="#475569" />
                  </button>
                ))
              )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}